import {
  CLOCK_SKEW_TOLERANCE_MS,
  EVIDENCE_STATUSES,
  FACTOR_DEFINITIONS,
  FACTOR_KEYS,
  FACTOR_STATUSES,
  RISK_LEVELS,
  SCANNER_VERSION,
  SNAPSHOT_KEYS,
  TOTAL_FACTOR_WEIGHT,
} from "./NinaScannerSchema";

const SYMBOL_PATTERN = /^[A-Z0-9.-]{1,10}$/;
const DIRECTIONS = ["Bullish bias", "Bearish bias", "Neutral bias"];
const ALERT_TYPES = ["opportunity", "risk"];
const MAX_PRICE = 1_000_000;
const MAX_RVOL = 1_000;
export const MARKET_DECIMAL_PLACES = 4;
export const PERCENT_DECIMAL_PLACES = 4;
export const RVOL_DECIMAL_PLACES = 4;
const MARKET_EPSILON = 1e-9;

export function validateScannerSnapshot(snapshot, options = {}) {
  const errors = [];
  const now = options.now ?? Date.now();

  if (!isPlainObject(snapshot)) {
    return { valid: false, errors: ["Scanner snapshot must be an object."] };
  }

  validateExactKeys(snapshot, SNAPSHOT_KEYS, "snapshot", errors);
  if (snapshot.schemaVersion !== SCANNER_VERSION) errors.push("Unsupported scanner schema.");
  if (!SYMBOL_PATTERN.test(snapshot.symbol || "")) errors.push("Invalid symbol.");
  if (typeof snapshot.setup !== "string" || !snapshot.setup.trim()) errors.push("Setup is required.");
  if (!DIRECTIONS.includes(snapshot.direction)) errors.push("Invalid market bias.");
  if (!ALERT_TYPES.includes(snapshot.alertType)) errors.push("Invalid alert type.");
  if (!RISK_LEVELS.includes(snapshot.riskLevel)) errors.push("Invalid risk level.");
  if (!EVIDENCE_STATUSES.includes(snapshot.evidenceStatus) ||
      snapshot.evidenceStatus === "unavailable") {
    errors.push("Invalid snapshot evidence status.");
  }
  if (typeof snapshot.source !== "string" || !snapshot.source.trim()) errors.push("Source is required.");
  if (!isTimestamp(snapshot.asOf)) {
    errors.push("A valid as-of timestamp is required.");
  } else if (Date.parse(snapshot.asOf) > now + CLOCK_SKEW_TOLERANCE_MS) {
    errors.push("As-of timestamp exceeds the permitted clock-skew tolerance.");
  }
  if (snapshot.demoLabel !== "Simulated") errors.push("Demo label must identify simulated data.");
  if (typeof snapshot.whyMoving !== "string" || !snapshot.whyMoving.trim()) {
    errors.push("A movement explanation is required.");
  }
  if (!isFiniteRange(snapshot.riskReward, 0, Number.MAX_SAFE_INTEGER)) {
    errors.push("Risk-to-reward must be a finite non-negative number.");
  }
  if (!isFiniteRange(snapshot.extensionPercent, 0, 100)) {
    errors.push("Extension must be a finite number between 0 and 100.");
  }
  if (!isFiniteRange(snapshot.stability, 0, 100)) {
    errors.push("Stability must be a finite number between 0 and 100.");
  }
  validateMarketFields(snapshot, now, errors);

  if (!isPlainObject(snapshot.factors)) {
    errors.push("Scanner factors are required.");
  } else {
    validateExactKeys(
      snapshot.factors,
      Object.keys(FACTOR_DEFINITIONS),
      "factors",
      errors
    );
    Object.keys(FACTOR_DEFINITIONS).forEach((key) => {
      validateFactor(snapshot.factors[key], key, errors);
    });
  }

  if (TOTAL_FACTOR_WEIGHT !== 100) errors.push("Scanner factor weights must total 100.");
  return { valid: errors.length === 0, errors };
}

function validateMarketFields(snapshot, now, errors) {
  [
    ["lastPrice", "Last price"],
    ["previousClose", "Previous close"],
    ["bid", "Bid"],
    ["ask", "Ask"],
    ["dayLow", "Day low"],
    ["dayHigh", "Day high"],
    ["openPrice", "Open price"],
  ].forEach(([key, label]) => {
    if (!isFiniteRange(snapshot[key], 0, MAX_PRICE) || snapshot[key] === 0) {
      errors.push(`${label} must be a finite positive number within the supported range.`);
    } else if (!isCanonicalDecimal(snapshot[key], MARKET_DECIMAL_PLACES)) {
      errors.push(`${label} must use no more than ${MARKET_DECIMAL_PLACES} decimal places.`);
    }
  });

  ["dollarChange", "percentageChange"].forEach((key) => {
    if (typeof snapshot[key] !== "number" || !Number.isFinite(snapshot[key])) {
      errors.push(`${key} must be a finite number.`);
    }
  });
  if (!isFiniteRange(snapshot.spread, 0, MAX_PRICE)) {
    errors.push("Spread must be a finite non-negative number.");
  }
  if (!Number.isSafeInteger(snapshot.volume) || snapshot.volume < 0) {
    errors.push("Volume must be a non-negative safe integer.");
  }
  if (!isFiniteRange(snapshot.relativeVolume, 0, MAX_RVOL)) {
    errors.push("Relative volume must be a finite non-negative number.");
  } else if (!isCanonicalDecimal(snapshot.relativeVolume, RVOL_DECIMAL_PLACES)) {
    errors.push(`Relative volume must use no more than ${RVOL_DECIMAL_PLACES} decimal places.`);
  }
  if (Number.isFinite(snapshot.bid) && Number.isFinite(snapshot.ask) &&
      snapshot.bid > snapshot.ask) {
    errors.push("Bid cannot be greater than ask.");
  }
  if (Number.isFinite(snapshot.dayLow) && Number.isFinite(snapshot.dayHigh) &&
      snapshot.dayLow > snapshot.dayHigh) {
    errors.push("Day low cannot be greater than day high.");
  }
  if (
    Number.isFinite(snapshot.lastPrice) &&
    Number.isFinite(snapshot.dayLow) &&
    Number.isFinite(snapshot.dayHigh) &&
    (snapshot.lastPrice < snapshot.dayLow || snapshot.lastPrice > snapshot.dayHigh)
  ) {
    errors.push("Last price must be within the declared day range.");
  }
  if (
    Number.isFinite(snapshot.openPrice) &&
    Number.isFinite(snapshot.dayLow) &&
    Number.isFinite(snapshot.dayHigh) &&
    (snapshot.openPrice < snapshot.dayLow || snapshot.openPrice > snapshot.dayHigh)
  ) {
    errors.push("Open price must be within the declared day range.");
  }

  const derived = deriveMarketFields(snapshot);
  [
    ["dollarChange", derived.dollarChange],
    ["percentageChange", derived.percentageChange],
    ["spread", derived.spread],
  ].forEach(([key, expected]) => {
    if (
      Number.isFinite(snapshot[key]) &&
      Number.isFinite(expected) &&
      Math.abs(snapshot[key] - expected) > MARKET_EPSILON
    ) {
      errors.push(`${key} is inconsistent with its validated base values.`);
    }
  });

  if (!isTimestamp(snapshot.updatedAt)) {
    errors.push("A valid updated timestamp is required.");
  } else if (Date.parse(snapshot.updatedAt) > now + CLOCK_SKEW_TOLERANCE_MS) {
    errors.push("Updated timestamp exceeds the permitted clock-skew tolerance.");
  }
}

export function deriveMarketFields(snapshot) {
  const rawDollarChange = snapshot.lastPrice - snapshot.previousClose;
  return {
    dollarChange: canonicalDecimal(rawDollarChange, MARKET_DECIMAL_PLACES),
    percentageChange: canonicalDecimal(
      (rawDollarChange / snapshot.previousClose) * 100,
      PERCENT_DECIMAL_PLACES
    ),
    spread: canonicalDecimal(snapshot.ask - snapshot.bid, MARKET_DECIMAL_PLACES),
  };
}

// Scanner Version 2 preserves prices, dollar changes, spreads, percentages,
// and RVOL at a documented maximum of four decimal places. All derivation,
// validation, sorting inputs, fixtures, and tests share this deterministic
// canonicalizer. Formatting for display is handled separately.
export function canonicalDecimal(value, decimalPlaces = MARKET_DECIMAL_PLACES) {
  if (typeof value !== "number" || !Number.isFinite(value)) return value;
  const factor = 10 ** decimalPlaces;
  const rounded = Math.round((value + Number.EPSILON) * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function isCanonicalDecimal(value, decimalPlaces) {
  return Math.abs(value - canonicalDecimal(value, decimalPlaces)) <= MARKET_EPSILON;
}

function validateFactor(factor, key, errors) {
  if (!isPlainObject(factor)) {
    errors.push(`${key} factor is required.`);
    return;
  }

  validateExactKeys(factor, FACTOR_KEYS, `${key} factor`, errors);
  if (!FACTOR_STATUSES.includes(factor.status)) errors.push(`${key} has an invalid status.`);
  if (!EVIDENCE_STATUSES.includes(factor.evidenceStatus)) {
    errors.push(`${key} has an invalid evidence status.`);
  }
  if (typeof factor.source !== "string" || !factor.source.trim()) {
    errors.push(`${key} requires a source.`);
  }
  if (typeof factor.explanation !== "string" || !factor.explanation.trim()) {
    errors.push(`${key} requires an explanation.`);
  }

  if (factor.status === "unavailable") {
    if (factor.score !== null) errors.push(`${key} unavailable evidence must use a null score.`);
    if (factor.evidenceStatus !== "unavailable") {
      errors.push(`${key} unavailable evidence must be labeled unavailable.`);
    }
    return;
  }

  if (!isFiniteRange(factor.score, 0, 100)) {
    errors.push(`${key} score must be a finite number between 0 and 100.`);
  }
  if (factor.evidenceStatus === "unavailable") {
    errors.push(`${key} available evidence cannot be labeled unavailable.`);
  }
  // Positive/negative labels must agree with the signed score. Conflicting
  // evidence may retain either net direction; disagreement is separately
  // reflected in Confidence and the visible conflict list.
  if (factor.status === "positive" && factor.score < 50) {
    errors.push(`${key} positive status cannot have a score below 50.`);
  }
  if (factor.status === "negative" && factor.score > 50) {
    errors.push(`${key} negative status cannot have a score above 50.`);
  }
}

function validateExactKeys(value, allowedKeys, label, errors) {
  const actual = Object.keys(value);
  actual
    .filter((key) => !allowedKeys.includes(key))
    .forEach((key) => errors.push(`${label} contains unexpected key: ${key}.`));
  allowedKeys
    .filter((key) => !Object.prototype.hasOwnProperty.call(value, key))
    .forEach((key) => errors.push(`${label} is missing required key: ${key}.`));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isFiniteRange(value, minimum, maximum) {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= minimum &&
    value <= maximum
  );
}

export function isTimestamp(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
